import MatchSortMenu from '../MatchSortMenu';
import AccountReminder from "../AccountReminder";
import useViewPreference from "../useViewPreference";
import useDismissMenu from "../useDismissMenu";
import DateNavigation from './DateNavigation';
import useLeagueSelection from "../useLeagueSelection";
import { buildLeagueFilters } from "../leagueFilters";
import { HQ } from "../sportConfig";
import ShareButton from "../ShareButton";
import useMatchSort from "../useMatchSort";
import NotificationButton from "./NotificationButton";
import FootballBadge from "../sports/FootballBadge";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "./MatchList.css";
import InstallApp from "./InstallApp";
import { useFavoriteTeams } from "../FavoriteTeams";
import { FollowMarker, followsGame, useFollowVersion } from "../FollowControls";
import { followFirstEnabled, isLiveGame, splitFollowed, useFollowSettingsVersion } from "../followLists";
import { isProtectedMatch, usePushProtection } from '../notificationProtection';

const orderMatches = (matches, order) => {
  if (order !== 'time') {
    // Keep provider competition order, but alphabetize gender divisions within
    // the same competition (Dam/Damer before Herr/Herrar).
    const groups = new Map();
    const division = (name = '') => name.toLocaleLowerCase('sv').replace(/\b(?:damer|dam|herrar|herr)\b/g, '').replace(/\s+/g, ' ').trim();
    matches.forEach((match) => {
      const key = division(match.league);
      if (!groups.has(key)) groups.set(key, groups.size);
    });
    return [...matches].sort((left, right) =>
      groups.get(division(left.league)) - groups.get(division(right.league)) ||
      (left.league || '').localeCompare(right.league || '', 'sv'));
  }
  const minutes = (value) => {
    const parts = /^(\d{1,2}):(\d{2})$/.exec(value || '');
    if (!parts || Number(parts[1]) > 23 || Number(parts[2]) > 59) return Infinity;
    return Number(parts[1]) * 60 + Number(parts[2]);
  };
  // Stable sorting retains provider order for equal or unknown start times.
  return [...matches].sort((left, right) => minutes(left.time) - minutes(right.time));
};

const MatchList = ({ matches, cacheInfo, error }) => {
  usePushProtection();
  const navigate = useNavigate();
  const { teamIds, ready, authenticated, notice } = useFavoriteTeams();
  useFollowVersion();
  useFollowSettingsVersion();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(()=>{const sync=()=>setHideResults((window.SportsHQSettings || window.localStorage).getItem(`${HQ.storagePrefix}-hide-results`)==='true');window.addEventListener('sports-hq-settings-state',sync);return ()=>window.removeEventListener('sports-hq-settings-state',sync);},[]);
  const [hideResults, setHideResults] = useState(
    () => (window.SportsHQSettings || window.localStorage).getItem(`${HQ.storagePrefix}-hide-results`) === "true",
  );
  const [leagueMenuOpen, setLeagueMenuOpen] = useState(false);
  const leagueMenuRef = useDismissMenu(leagueMenuOpen, setLeagueMenuOpen);
  const [allLeagues, setAllLeagues] = useState([]);
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${HQ.api}/sportsim/simulations`, { signal: controller.signal })
      .then((response) => { if (!response.ok) throw new Error(); return response.json(); })
      .then((rows) => setAllLeagues(rows))
      .catch((error) => { if (error.name !== "AbortError") setAllLeagues([]); });
    return () => controller.abort();
  }, []);

  // Hämta dagens datum i YYYY-MM-DD format
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const lastDailyDate = new Date(now);
  lastDailyDate.setDate(lastDailyDate.getDate() + 7);
  const lastDailyDateValue = `${lastDailyDate.getFullYear()}-${String(lastDailyDate.getMonth() + 1).padStart(2, "0")}-${String(lastDailyDate.getDate()).padStart(2, "0")}`;

  // Hämta valt datum från URL eller sätt till idag om inget anges
  const queryDate = searchParams.get("date");
  const selectedDate = queryDate ? queryDate : today;
  const selectedDateLabel = new Intl.DateTimeFormat("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(selectedDate.slice(0, 4) !== today.slice(0, 4) ? { year: "numeric" } : {}),
  }).format(new Date(`${selectedDate}T12:00:00`)).replace(".", "");
  const [selectedLeagues, saveLeagues] = useLeagueSelection(searchParams, "daily");
  const [savedMine,saveMine]=useViewPreference(`${HQ.storagePrefix}-daily-mine`,"0");
  const mineOnly = (searchParams.has("mine")?searchParams.get("mine"):savedMine) === "1";
  const { savedOrder, saveOrder, ready: sortReady, notice: sortNotice } = useMatchSort();
  const requestedOrder = searchParams.get("sort");
  const sortOrder = requestedOrder === 'time' || requestedOrder === 'league' ? requestedOrder : savedOrder;
  const leagueFilters = useMemo(() => buildLeagueFilters(matches || [], allLeagues), [matches, allLeagues]);
  const leagues = leagueFilters.options;
  const firstOtherLeague = leagues.findIndex((league) => !league.daily);
  const filteredMatches = (matches || []).filter((match) =>
    leagueFilters.matchesSelection(match, selectedLeagues)
    && (!mineOnly || teamIds.includes(match.home_team_id) || teamIds.includes(match.away_team_id) || followsGame(match))
  );

  const updateParams = (updates) => {
    const nextParams = new URLSearchParams(searchParams);
    if (Object.prototype.hasOwnProperty.call(updates,"mine")) saveMine(updates.mine || "0");
    if (Object.prototype.hasOwnProperty.call(updates, "league")) saveLeagues(updates.league ? [updates.league] : []);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) nextParams.set(key, value);
      else if(key === "mine")nextParams.set(key,"0");
      else nextParams.delete(key);
    });
    setSearchParams(nextParams, {replace:true});
  };

  const toggleLeague = (league) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("league");
    const nextLeagues = leagueFilters.selected(league, selectedLeagues)
      ? selectedLeagues.filter((item) => !leagueFilters.aliases.get(item)?.has(league.key))
      : [...selectedLeagues, league.name];
    saveLeagues(nextLeagues);
    nextLeagues.forEach((item) => nextParams.append("league", item));
    setSearchParams(nextParams, {replace:true});
  };

  const [pushNotice, setPushNotice] = useState('');
  const toggleHideResults = async () => {
    const nextValue = !hideResults;
    (window.SportsHQSettings || window.localStorage).setItem(`${HQ.storagePrefix}-hide-results`, String(nextValue));
    setHideResults(nextValue);
    try {
      if (HQ.capabilities.notifications) await window.SportsHQPush?.setHidden(HQ.sport, nextValue);
      setPushNotice('');
    } catch {
      setPushNotice('Resultat döljs här, men notisskyddet kunde inte sparas. Öppna Notiser och försök igen.');
    }
  };

  // Uppdatera URL vid datumändring
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    const formattedDate = newDate.toISOString().split("T")[0];
    updateParams({ date: formattedDate });
  };

  // Navigera till dagens datum
  const goToToday = () => {
    if (selectedDate !== today) {
      updateParams({ date: "" });
    }
  };

  if (error) {
    return <p className="error-message">⚠️ Det gick inte att hämta matcherna. Prova att ladda om sidan.</p>;
  }

  if (!matches) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p className="loading-text">Hämtar aktuella matcher, ett ögonblick...</p>
      </div>
    );
  }

  const isFollowedMatch = (match) => teamIds.includes(match.home_team_id) || teamIds.includes(match.away_team_id) || followsGame(match);
  const ordered = orderMatches(filteredMatches, sortOrder);
  // "Du följer" first: only when something is followed, the profile switch is on and the list is not already filtered to it.
  const split = !mineOnly && followFirstEnabled() ? splitFollowed(ordered, isFollowedMatch) : null;
  const sections = split && split.followed.length ? split : null;
  const renderCard = (match, inSection) => {
    const followed = isFollowedMatch(match), live = followed && isLiveGame(match);
    return (
          <div
            key={match.id}
            className={`match-card${live ? " match-card--followed-live" : ""}`}
            onClick={() =>
              navigate(
                `/match?url=${encodeURIComponent(match.match_link + "/laguppstallning")}&returnTo=${encodeURIComponent(`/${searchParams.toString() ? `?${searchParams.toString()}` : ""}`)}`,
                { state: { match } },
              )
            }
          >
            <div className="league-info">
              {match.league_logo && (
                <img src={match.league_logo} alt={match.league} className="league-logo" />
              )}
              {match.league}
            </div>
            <FollowMarker match={match} live={live} marker={!inSection} />

            <div className="match-details">
              <div className="team">
                {HQ.sport === "football" ? <FootballBadge src={match.home_logo} name={match.home_team}/> : <img src={match.home_logo} alt={match.home_team} className="team-logo" />}
                <span className="team-name">{match.home_team}</span>
              </div>

              <div className="match-time">
                {(hideResults || isProtectedMatch(match)) && (match.status?.status_id > 1 || (HQ.sport === "football" && match.status?.home_goals != null)) ? (
                  <span className="hidden-result">Resultat dolt</span>
                ) : (match.status?.status_id > 1 || (HQ.sport === "football" && match.status?.home_goals != null)) && match.status?.home_goals !== null
                  ? `${match.status.home_goals} – ${match.status.away_goals}`
                  : match.time}
                {!hideResults && !isProtectedMatch(match) && (match.status?.status_id > 1 || (HQ.sport === "football" && match.status?.home_goals != null)) && (
                  <span className={`match-status match-status--${match.status.status_id}`}>
                    {match.status.label}
                  </span>
                )}
              </div>

              <div className="team">
                <span className="team-name">{match.away_team}</span>
                {HQ.sport === "football" ? <FootballBadge src={match.away_logo} name={match.away_team}/> : <img src={match.away_logo} alt={match.away_team} className="team-logo" />}
              </div>
            </div>
          </div>
    );
  };
  return (
    <div className="match-list-container">
      {/* Datum-navigering */}
      <DateNavigation label={selectedDateLabel} isToday={selectedDate === today}
        onPrevious={() => changeDate(-1)} onNext={() => changeDate(1)}
        nextDisabled={selectedDate >= lastDailyDateValue} onToday={goToToday} />

      <div className="home-meta"><div className="daily-actions">
        <Link className="tips-entry" to="/series">Serier</Link>
        <Link className="tips-entry" to="/tips">Tippa</Link>
        <MatchSortMenu value={sortOrder} disabled={!sortReady} onChange={order => { saveOrder(order); updateParams({ sort: order }); }} />
        <div className="hq-home-notification-actions"><ShareButton path={`/?date=${selectedDate}`} title={`${HQ.label} · ${selectedDateLabel}`} /><NotificationButton day={selectedDate} /></div>
        </div>
      </div>

      {matches && (
        <div className="match-controls">
          {(leagues.length > 0 || selectedLeagues.length > 0) && <div className="match-filter match-filter--leagues">
              <span className="match-filter__label">Serier</span>
              <div className="league-dropdown" ref={leagueMenuRef}>
                <button type="button" className="league-dropdown__trigger" aria-expanded={leagueMenuOpen}
                  aria-controls="home-league-options" onClick={() => setLeagueMenuOpen((open) => !open)}>
                  <span className="league-dropdown__summary-full">{selectedLeagues.length ? `${selectedLeagues.length} serier valda` : "Alla serier"}</span>
                  <span className="league-dropdown__summary-short">{selectedLeagues.length ? `${selectedLeagues.length} valda` : "Alla serier"}</span>
                  <span aria-hidden="true">▾</span>
                </button>
                {selectedLeagues.length > 0 && <button type="button" className="league-dropdown__clear"
                  onClick={() => updateParams({ league: "" })} aria-label="Rensa serieval">Rensa</button>}
                {leagueMenuOpen && <div id="home-league-options" className="league-options" role="group" aria-label="Filtrera på serier">
                  {leagues.map((league, index) => <React.Fragment key={league.key}>
                    {index === 0 && leagues.some((item) => item.daily) && <div className="league-options__heading">Matcher {selectedDate === today ? "idag" : "vald dag"}</div>}
                    {index === firstOtherLeague && <div className="league-options__heading league-options__heading--other">Övriga serier</div>}
                    <label className="league-option">
                    <input type="checkbox" checked={leagueFilters.selected(league, selectedLeagues)} onChange={() => toggleLeague(league)} />
                    <span>{league.name}</span>
                  </label></React.Fragment>)}
                </div>}
              </div>
              {selectedLeagues.length > 0 && <span className="match-filter__count">{filteredMatches.length} matcher</span>}
          </div>}
          <button type="button" className={`favorite-filter${mineOnly ? " favorite-filter--active" : ""}`}
            aria-label={'Visa bara det jag följer'} aria-pressed={mineOnly} disabled={!ready} onClick={() => updateParams({ mine: mineOnly ? "" : "1" })}>
            <span aria-hidden="true">★</span> {'Mina'}
          </button>
          <button
            type="button"
            className={`spoiler-toggle${hideResults ? " spoiler-toggle--active" : ""}`}
            aria-pressed={hideResults}
            onClick={toggleHideResults}
          >
            <span>Dölj resultat</span>
            <span className="spoiler-toggle__track" aria-hidden="true">
              <span className="spoiler-toggle__thumb" />
            </span>
          </button>
        </div>
      )}

      <div className="daily-meta">
        {cacheInfo?.updated_at && (
          <p className="cache-status">
            Uppdaterad {new Date(cacheInfo.updated_at).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
            {cacheInfo.refreshing ? " · hämtar nytt i bakgrunden…" : ""}
          </p>
        )}
        <InstallApp />
      </div>

      <AccountReminder />
      {notice && <p className="favorites-notice" role="status">{notice}</p>}
      {sortNotice && <p className="favorites-notice" role="status">{sortNotice}</p>}

      {matches.length === 0 && (
        <p className="no-matches">Inga matcher är registrerade för det här datumet.</p>
      )}

      {matches.length > 0 && filteredMatches.length === 0 && (
        <div className="no-filtered-matches">
          <p>{mineOnly && !teamIds.length && !window.SportsHQFollows?.followedGames().length ? 'Du följer inget än. Tryck på stjärnan vid ett lag eller en match.' : mineOnly ? 'Det du följer har inga matcher den här dagen.' : "Inga matcher i den valda serien."}</p>
          {mineOnly && !authenticated && <small>Det du följer sparas på den här enheten.</small>}
          <button type="button" onClick={() => updateParams({ league: "", mine: "" })}>Visa alla matcher</button>
        </div>
      )}

      <div className="match-list match-list--compact">
        {pushNotice && <p role="alert">{pushNotice}</p>}
        {sections ? <>
          <h2 className="follow-section__title"><span aria-hidden="true">★</span>{'Du följer'}</h2>
          {sections.followed.map((match) => renderCard(match, true))}
          <h2 className="follow-section__title follow-section__title--rest">{'Alla matcher'}</h2>
          {sections.rest.map((match) => renderCard(match, false))}
        </> : ordered.map((match) => renderCard(match, false))}
      </div>
    </div>
  );
};

export default MatchList;
